import React from 'react';
import { Modal, Card, Typography, Button, Row, Col, Tag, Divider, List, Space } from 'antd';
import {
  CrownOutlined,
  CheckCircleFilled,
  LockOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { useScopedNavigate } from './MarketerView/useScopedNavigate';
import { useGetAnnualPromotionQuery } from '../services/nestjs-base-api';
import { useAuth } from '../hooks/useAuth';

const { Title, Text, Paragraph } = Typography;

interface TierPlan {
  name: string;
  code: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  color: string;
}

const getTierPlans = (monthlyPrice: number): TierPlan[] => [
  {
    name: 'Free',
    code: 'FREE',
    price: 'Free',
    description: 'Get started with basic brand equity tracking',
    color: '#52c41a',
    features: [
      'Survey functionality',
      'Basic BE model elements',
      'Overall benchmarking',
      'Basic visualizations',
    ],
  },
  {
    name: 'Professional',
    code: 'PROFESSIONAL',
    price: `$${monthlyPrice.toLocaleString()}/mo`,
    description: 'Full access to brand equity insights',
    color: '#1890ff',
    highlighted: true,
    features: [
      'All Free plan features',
      'Full BE model access',
      'Product type benchmarking',
      '20 AI Expert credits/month',
      'AI Experts access',
      'Priority support',
    ],
  },
  // Enterprise plan hidden until customer segmentation is ready
  // {
  //   name: 'Enterprise',
  //   code: 'ENTERPRISE',
  //   price: 'Contact us',
  //   description: 'Complete solution for enterprise needs',
  //   color: '#722ed1',
  //   features: [
  //     'All Professional features',
  //     'All benchmarking levels',
  //     '100 AI Expert credits/month',
  //     '10 segmentation fields',
  //     'Priority support',
  //     'Custom integrations',
  //   ],
  // },
];

interface TierUpgradePromptProps {
  /** Feature that requires upgrade */
  featureName: string;
  /** Description of what the feature provides */
  featureDescription?: string;
  /** Required plan to access the feature */
  requiredPlan: 'Professional' | 'Enterprise';
  /** Current plan (if known) */
  currentPlan?: string;
  /** Whether to show as a modal */
  isModal?: boolean;
  /** Modal visibility state */
  visible?: boolean;
  /** Modal close handler */
  onClose?: () => void;
  /** Custom upgrade handler */
  onUpgrade?: (plan: string) => void;
  /** Show compact version */
  compact?: boolean;
}

/**
 * Prompt component for encouraging tier upgrades
 *
 * @example
 * // Inline prompt
 * <TierUpgradePrompt
 *   featureName="Product Type Benchmarking"
 *   requiredPlan="Professional"
 * />
 *
 * @example
 * // Modal prompt
 * <TierUpgradePrompt
 *   featureName="AI Experts"
 *   requiredPlan="Professional"
 *   isModal
 *   visible={showModal}
 *   onClose={() => setShowModal(false)}
 * />
 */
export const TierUpgradePrompt: React.FC<TierUpgradePromptProps> = ({
  featureName,
  featureDescription,
  requiredPlan,
  currentPlan,
  isModal = false,
  visible,
  onClose,
  onUpgrade,
  compact = false,
}) => {
  const navigate = useScopedNavigate();
  const { user } = useAuth();

  // Fetch pricing from system settings via API
  const { data: annualPromotion } = useGetAnnualPromotionQuery(
    { jwtToken: user?.jwtToken || '' },
    { skip: !user?.jwtToken }
  );
  const monthlyPrice = annualPromotion?.pricing?.monthlyPrice || 990;
  const tierPlans = getTierPlans(monthlyPrice);
  const planConfig = tierPlans.find((p) => p.name === requiredPlan) || tierPlans[1];

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade(planConfig.code);
    } else {
      navigate('/dashboard/organisation?tab=billing&showUpgrade=true');
    }
    onClose?.();
  };

  const content = (
    <div style={{ textAlign: 'center', padding: compact ? 16 : 24 }}>
      <div
        style={{
          width: compact ? 60 : 80,
          height: compact ? 60 : 80,
          borderRadius: '50%',
          background: `${planConfig.color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}
      >
        <LockOutlined
          style={{
            fontSize: compact ? 28 : 36,
            color: planConfig.color,
          }}
        />
      </div>

      <Title level={compact ? 5 : 4} style={{ marginBottom: 8 }}>
        Unlock {featureName}
      </Title>

      {featureDescription && (
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {featureDescription}
        </Paragraph>
      )}

      <Tag
        color={planConfig.color}
        style={{ fontSize: 14, padding: '4px 12px', marginBottom: 16 }}
      >
        <CrownOutlined style={{ marginRight: 6 }} />
        Requires {planConfig.name} Plan
      </Tag>

      {!compact && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <List
            size="small"
            dataSource={planConfig.features.slice(0, 4)}
            renderItem={(feature) => (
              <List.Item style={{ padding: '8px 0', border: 'none' }}>
                <Space>
                  <CheckCircleFilled style={{ color: '#52c41a' }} />
                  <Text>{feature}</Text>
                </Space>
              </List.Item>
            )}
            style={{ textAlign: 'left', maxWidth: 300, margin: '0 auto' }}
          />
        </>
      )}

      <div style={{ marginTop: 20 }}>
        <Button
          type="primary"
          size={compact ? 'middle' : 'large'}
          onClick={handleUpgrade}
          icon={<RocketOutlined />}
          style={{
            background: planConfig.color,
            borderColor: planConfig.color,
          }}
        >
          Upgrade to {planConfig.name}
        </Button>
        {planConfig.price !== 'Contact us' && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Starting at {planConfig.price}
          </Text>
        )}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={compact ? 400 : 500}
        centered
      >
        {content}
      </Modal>
    );
  }

  return (
    <Card
      style={{
        border: `1px solid ${planConfig.color}40`,
        borderRadius: 12,
      }}
    >
      {content}
    </Card>
  );
};

/**
 * Full page tier comparison component
 */
interface TierComparisonProps {
  currentPlan?: string;
  onSelectPlan?: (planCode: string) => void;
}

export const TierComparison: React.FC<TierComparisonProps> = ({
  currentPlan,
  onSelectPlan,
}) => {
  const navigate = useScopedNavigate();
  const { user } = useAuth();

  // Fetch pricing from system settings via API
  const { data: annualPromotion } = useGetAnnualPromotionQuery(
    { jwtToken: user?.jwtToken || '' },
    { skip: !user?.jwtToken }
  );
  const monthlyPrice = annualPromotion?.pricing?.monthlyPrice || 990;
  const tierPlans = getTierPlans(monthlyPrice);

  const handleSelect = (planCode: string) => {
    if (onSelectPlan) {
      onSelectPlan(planCode);
    } else {
      navigate('/dashboard/organisation?tab=billing&showUpgrade=true');
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <Title level={2}>Choose Your Plan</Title>
        <Paragraph type="secondary" style={{ fontSize: 16 }}>
          Unlock powerful features to grow your brand
        </Paragraph>
      </div>

      <Row gutter={24} justify="center">
        {tierPlans.map((plan) => (
          <Col key={plan.code} xs={24} sm={12} md={8}>
            <Card
              style={{
                height: '100%',
                borderRadius: 12,
                border: plan.highlighted
                  ? `2px solid ${plan.color}`
                  : '1px solid #f0f0f0',
                position: 'relative',
              }}
              styles={{ body: { padding: 24 } }}
            >
              {plan.highlighted && (
                <div
                  style={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                >
                  <Tag color={plan.color}>Most Popular</Tag>
                </div>
              )}

              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Title level={4} style={{ color: plan.color, marginBottom: 4 }}>
                  {plan.name}
                </Title>
                <Title level={2} style={{ margin: 0 }}>
                  {plan.price}
                </Title>
                <Text type="secondary">{plan.description}</Text>
              </div>

              <List
                size="small"
                dataSource={plan.features}
                renderItem={(feature) => (
                  <List.Item style={{ padding: '8px 0', border: 'none' }}>
                    <Space>
                      <CheckCircleFilled style={{ color: '#52c41a' }} />
                      <Text>{feature}</Text>
                    </Space>
                  </List.Item>
                )}
              />

              <div style={{ marginTop: 24, textAlign: 'center' }}>
                <Button
                  type={plan.highlighted ? 'primary' : 'default'}
                  size="large"
                  block
                  onClick={() => handleSelect(plan.code)}
                  disabled={currentPlan?.startsWith(plan.code)}
                  style={
                    plan.highlighted
                      ? { background: plan.color, borderColor: plan.color }
                      : {}
                  }
                >
                  {currentPlan?.startsWith(plan.code)
                    ? 'Current Plan'
                    : plan.price === 'Contact us'
                    ? 'Contact Sales'
                    : 'Get Started'}
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

/**
 * Inline upgrade badge for feature headers
 */
interface UpgradeBadgeProps {
  requiredPlan: 'Professional' | 'Enterprise';
  onClick?: () => void;
}

export const UpgradeBadge: React.FC<UpgradeBadgeProps> = ({
  requiredPlan,
  onClick,
}) => {
  const colors = {
    Professional: '#1890ff',
    Enterprise: '#722ed1',
  };

  return (
    <Tag
      color={colors[requiredPlan]}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <CrownOutlined style={{ marginRight: 4 }} />
      {requiredPlan}
    </Tag>
  );
};

export default TierUpgradePrompt;
